<template>
    <div class="setting-page-content-wrapper features">
        <div class="page-header">
            <div class="title">{{ $t("message.availability") }}</div>
            <div class="subtitle">{{ $t("fastWizard.onlineSupportVisitorTime") }}</div>
        </div>
        <section>
            <div class="features">
                <div class="feature no-background">
                    <div>
                        <a-row type="flex" justify="center" align="top">
                            <a-col class="mt-3 mb-3">
                                <AvailabilityConfig />
                                <div class="offline-button">
                                    <FeatureItem
                                        v-model="offlineMode"
                                        :title="$t('message.offlineMode')"
                                        :description="$t('message.vtipyOfflineMode')"
                                        icon="offlineIcon"
                                    />
                                    <a-col class="mt-5 my-auto p-3">
                                        <a-button
                                            v-model="editMode"
                                            v-tippy="{
                                                followCursor: true,
                                                placement: 'bottom-end',
                                            }"
                                            :content="$t('message.tooltipOSRForm')"
                                            class="custom-field-button btnColor"
                                            style="width: 100%"
                                            @click="showModal"
                                            danger
                                            ghost
                                            >{{ $t("message.changeOSRForm") }}
                                        </a-button>
                                        <Modal2
                                            :show-footer="false"
                                            v-if="isModalShow"
                                            id="osr-form"
                                            :title="editMode ? $t('message.updateOSRForm') : $t('message.newOSRForm')"
                                            @cancel="hideModal"
                                        >
                                            <a-alert
                                                v-if="$validator.errors.items.length && submitClicked"
                                                :key="`alertContainer-${$i18n.locale}`"
                                                :message="$t('message.error')"
                                                class="mb-2"
                                                showIcon
                                                type="error"
                                            >
                                                <div slot="description">
                                                    <div v-for="err of $validator.errors.items">
                                                        <span>{{ err.msg }}</span>
                                                    </div>
                                                </div>
                                            </a-alert>
                                            <b-form
                                                class="d-flex flex-column justify-content-center"
                                                @submit.prevent="handleSaveOsrForm"
                                            >
                                                <b-row>
                                                    <b-col md="12">
                                                        <b-row>
                                                            <b-col>
                                                                <MultiContent
                                                                    ref="MultiContent"
                                                                    v-model="osrInitialObject"
                                                                />
                                                            </b-col>
                                                            <b-col md="12 mt-2">
                                                                <b-button
                                                                    :disabled="loading"
                                                                    type="submit"
                                                                    variant="primary"
                                                                >
                                                                    <template v-if="!loading">
                                                                        <span v-if="!editMode">{{
                                                                            $t("message.save")
                                                                        }}</span>
                                                                        <span v-else>{{ $t("message.update") }}</span>
                                                                    </template>
                                                                    <template v-else>
                                                                        <font-awesome-icon icon="spinner" spin />
                                                                    </template>
                                                                </b-button>
                                                                <b-button @click="cancelForm()">
                                                                    <template>{{ $t("message.cancel") }}</template>
                                                                </b-button>
                                                            </b-col>
                                                        </b-row>
                                                    </b-col>
                                                </b-row>
                                            </b-form>
                                        </Modal2>
                                    </a-col>
                                </div>
                            </a-col>
                            <a-col></a-col>
                        </a-row>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import log2 from "../../../../../utils/log2";
import WirelessKeyboardIcon from "../../../../../components/svg/icons/WirelessKeyboardIcon.vue";
import FeatureItem from "../../components/FeatureItem.vue";
import AvailabilityConfig from "../../../../../components/fast-initialize/AvailabilityConfig.vue";
import * as _ from "lodash";
import { toArray } from "../../../../../utils/languageContent";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import MultiContent from "../../../../../components/MultiContent.vue";
import { faPencilAlt, faPlusCircle, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import CONFIG from "../../../../../config/config";
import Modal2 from "../../../../../components/common/Modal2.vue";

library.add(faPencilAlt, faTimes, faPlusCircle, faSpinner);

const osrtInitialObject = {
    _id: null,
    languageContent: {
        tr: {
            title: "Çevrimdışı",
            content:
                "Hiç bir temsilcimiz şuanda aktif değil. Mesajınızı ileterek size mail yoluyla ulaşmamızı ister misiniz?",
        },
        en: {
            title: "Offline",
            content: "There is no active representative right now. We can reach you with email if you send a message.",
        },
        ru: {
            title: "не в сети",
            content:
                "Сейчас активного представителя нет. Мы свяжемся с вами по электронной почте, если вы отправите сообщение.",
        },
        de: {
            title: "Offline",
            content:
                "Keiner unserer Agenten ist derzeit aktiv. Möchten Sie, dass wir Sie per E-Mail erreichen, indem wir Ihre Nachricht weiterleiten?",
        },
        fr: {
            title: "Hors ligne",
            content:
                "Aucun de nos agents n'est actif pour le moment. Souhaitez-vous que nous vous contactions par e-mail en transmettant votre message?",
        },
        es: {
            title: "Desconectado",
            content:
                "Ninguno de nuestros agentes está activo en este momento. Quiere que le contactemos por correo electrónico reenviando su mensaje?",
        },
        ar: {
            title: "غير متصل على الانترنت",
            content:
                "لا أحد من وكلائنا نشط في الوقت الحالي. هل تود أن نتواصل معك عبر البريد الإلكتروني عن طريق إعادة توجيه رسالتك؟",
        },
        it: {
            title: "Offline",
            content:
                "Nessun nostro agente è attivo al momento. Vuoi che ti contattiamo via e-mail inoltrando il tuo messaggio?",
        },
    },
};

const log = log2("Settings/Availability");

export default {
    name: "Availability",
    components: {
        WirelessKeyboardIcon,
        FeatureItem,
        AvailabilityConfig,
        MultiContent,
        Modal2,
    },
    props: {
        updateSettings: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            submitClicked: false,
            loading: false,
            editMode: false,
            tippyOptions: {
                followCursor: true,
                placement: "bottom-end",
            },
            localOsrLabel: _.cloneDeep(osrtInitialObject),
            isModalShow: false,
        };
    },

    computed: {
        ...mapState({
            settings: (state) => state.setting.settings,
            site: (state) => state.site.site,
            systemLanguages: (state) => state.setting.settings.systemLanguages,
            settingInitialized: (state) => state.setting.initialized,
        }),
        offlineMode: {
            get() {
                return this.settings.offlineMode;
            },
            set(value) {
                this.setValue("offlineMode", value);
            },
        },
        osrInitialObject: {
            get() {
                if (!this.systemLanguages) {
                    return [];
                }
                const languageContent = this.settings.osrLabels.languageContent;
                const osrLabelHash =
                    languageContent.length > 0
                        ? languageContent.reduce((acc, item) => {
                              acc[item.languageCode] = {
                                  title: item.title,
                                  content: item.content,
                              };
                              return acc;
                          }, {})
                        : this.localOsrLabel;
                return [
                    {
                        type: "input",
                        field: "title",
                        label: this.$t("message.message"),
                        placeholder: this.$t("message.message"),
                        data: this.systemLanguages.reduce((acc, lang) => {
                            acc[lang] = _.get(osrLabelHash, `[${lang}].title`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                    {
                        type: "rich",
                        field: "content",
                        label: this.$t("message.message"),
                        placeholder: this.$t("message.message"),
                        data: this.systemLanguages.reduce((acc, lang) => {
                            acc[lang] = _.get(osrLabelHash, `[${lang}].content`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                ];
            },
            set(v) {
                this.localOsrLabel.languageContent = this.systemLanguages.reduce((acc, lang) => {
                    acc[lang] = {};
                    for (const _v of v) acc[lang][_v.field] = _v.data[lang];
                    return acc;
                }, {});
            },
        },
    },
    methods: {
        showModal() {
            this.isModalShow = true;
        },
        hideModal() {
            this.isModalShow = false;
        },
        setValue(key, value) {
            this.updateSettings(key, value);
        },
        handleSaveOsrForm() {
            this.submitClicked = true;
            this.$validator.validateAll().then(async (response) => {
                if (response) {
                    this.loading = true;
                    let newValue = toArray(this.localOsrLabel);
                    delete newValue._id;
                    await this.updateSettings("osrLabels", newValue);
                    /*this.$bvModal.hide("osr-form");*/
                    this.hideModal();
                    this.loading = false;
                    this.editMode = false;
                    this.submitClicked = false;
                } else {
                    log("validation error", response, this.$validator);
                    this.loading = false;
                }
            });
        },
        cancelForm() {
            /*this.$bvModal.hide("osr-form");*/
            this.hideModal();
            this.editMode = false;
        },
    },
    mounted() {
        this.mountedTime = Date.now();
    },
    updated() {
        // log("updated");
        if (this.$validator.locale !== this.$i18n.locale) {
            this.$validator.localize(this.$i18n.locale);
            if (this.submitClicked) this.$validator.validateAll();
            this.$forceUpdate();
        }
    },
    watch: {
        editMode(newVal) {
            this.editMode ? this.showModal() : this.hideModal();
        },
    },
};
</script>

<style scoped lang="scss">
.features {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.feature {
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 10px;
    border: 1px solid var(--color-border-default);
    border-radius: 4px;
    background: var(--color-bg-neutral-default);
    max-width: 900px;

    .left {
        display: flex;
        align-items: center;
        gap: 20px;

        .icon {
            svg {
                width: 40px;
                height: 40px;
                fill: var(--color-bg-primary-default);
            }
        }

        .feature-title {
            font-size: 16px;
            font-weight: 500;
            color: var(--color-text-strong-default);
        }

        .feature-description {
            font-size: 14px;
            font-weight: 400;
            color: var(--color-text-low-default);
        }
    }
}

.dark-mode .feature {
    background: #1e1e2d;

    .left {
        display: flex;
        align-items: center;
        gap: 20px;

        .icon {
            svg {
                width: 40px;
                height: 40px;
                fill: var(--color-bg-primary-default);
            }
        }

        .feature-title {
            font-size: 16px;
            font-weight: 500;
            color: #fff;
        }

        .feature-description {
            font-size: 14px;
            font-weight: 400;
            color: var(--color-text-low-default);
        }
    }
}

.errSpan {
    font-size: 8px;
    color: var(--color-bg-critical-default);
}

.right {
    display: flex !important;
    align-items: center !important;

    .ant-switch-checked {
        background-color: var(--color-bg-primary-default);
    }
}

.no-background {
    background-color: transparent !important;
    border: transparent !important;
}

.btnColor {
    border-color: var(--color-bg-primary-default) !important;
    color: var(--color-bg-primary-default) !important;
}

.offline-button {
    padding-top: 10px;
    display: flex;
}
</style>
